import { lerpColor } from '../../color';
import { hsl, type World as Look } from '../../world';
import { calmCenter, glowDot, grain, rgba, sketchCircle, sketchLine, vignette, type Painter } from '../../art/paint';

const TAU = Math.PI * 2;
const UP = -Math.PI / 2;

interface Disc {
  readonly x: number;
  readonly y: number;
  readonly r: number;
}

interface Train {
  readonly gear: Disc;
  readonly pinion: Disc;
  readonly wheel: Disc;
}

interface Scene {
  readonly trains: readonly Train[];
  readonly focal: Disc;
  readonly clocks: readonly Disc[];
  readonly top: number;
  readonly loupe: Disc;
  readonly screws: Disc;
  readonly spring: Disc;
  readonly tweezers: Disc;
}

interface Tones {
  readonly bg: number;
  readonly far: number;
  readonly ceil: number;
  readonly ink: number;
  readonly brass: number;
  readonly brassLit: number;
  readonly wood: number;
  readonly mark: number;
}

function mesh(gear: Disc, angle: number, r: number): Disc {
  const d = (gear.r + r) * 0.9;
  return { x: gear.x + Math.cos(angle) * d, y: gear.y + Math.sin(angle) * d, r };
}

function train(gear: Disc, pinionAt: number, pinionK: number, wheelAt: number, wheelK: number): Train {
  return { gear, pinion: mesh(gear, pinionAt, gear.r * pinionK), wheel: mesh(gear, wheelAt, gear.r * wheelK) };
}

function sceneOf(p: Painter): Scene {
  const { w, h, unit: u } = p;
  if (w / h < 0.8) {
    const fr = Math.min(0.26 * u, 0.055 * h);
    const left = { x: 0, y: 0.44 * h, r: 0.24 * u };
    const right = { x: 0, y: 0.63 * h, r: 0.18 * u };
    const top = 0.78 * h;
    return {
      trains: [
        train({ ...left, x: Math.max(left.r * 1.12, 0.1 * w) }, 0.2, 0.48, -1.2, 0.34),
        train({ ...right, x: Math.min(w - right.r * 1.12, 0.9 * w) }, Math.PI + 0.35, 0.5, -1.75, 0.36),
      ],
      focal: { x: 0.3 * w, y: 0.27 * h, r: fr },
      clocks: [
        { x: 0.64 * w, y: 0.225 * h, r: fr * 0.55 },
        { x: 0.86 * w, y: 0.3 * h, r: fr * 0.42 },
        { x: 0.09 * w, y: 0.215 * h, r: fr * 0.32 },
      ],
      top,
      loupe: { x: 0.72 * w, y: top + 0.05 * h, r: 0.1 * u },
      screws: { x: 0.42 * w, y: top + 0.055 * h, r: 0.022 * u },
      spring: { x: 0.12 * w, y: top + 0.04 * h, r: 0.045 * u },
      tweezers: { x: 0.52 * w, y: top + 0.08 * h, r: 0.16 * u },
    };
  }
  const left = { x: 0, y: 0.52 * h, r: 0.4 * u };
  const right = { x: 0, y: 0.66 * h, r: 0.3 * u };
  const top = 0.885 * h;
  const lx = Math.max(left.r * 1.1, 0.13 * w);
  const rx = Math.min(w - right.r * 1.1, 0.88 * w);
  const fr = 0.19 * u;
  return {
    trains: [train({ ...left, x: lx }, -0.95, 0.48, 2.0, 0.32), train({ ...right, x: rx }, -2.4, 0.5, -1.3, 0.38)],
    focal: { x: Math.min(w - fr * 1.3, 0.86 * w), y: 0.33 * h, r: fr },
    clocks: [
      { x: 0.27 * w, y: 0.79 * h, r: 0.08 * u },
      { x: 0.71 * w, y: 0.81 * h, r: 0.065 * u },
      { x: Math.max(0.06 * u, 0.045 * w), y: 0.84 * h, r: 0.05 * u },
    ],
    top,
    loupe: { x: 0.2 * w, y: top + 0.045 * h, r: 0.075 * u },
    screws: { x: 0.36 * w, y: top + 0.05 * h, r: 0.016 * u },
    spring: { x: 0.77 * w, y: top + 0.04 * h, r: 0.035 * u },
    tweezers: { x: 0.56 * w, y: top + 0.07 * h, r: 0.12 * u },
  };
}

function wall(p: Painter, t: Tones): void {
  const ctx = p.ctx;
  const g = ctx.createLinearGradient(0, 0, p.w * 0.2, p.h);
  g.addColorStop(0, rgba(lerpColor(t.bg, t.ink, 0.35)));
  g.addColorStop(0.55, rgba(t.bg));
  g.addColorStop(1, rgba(lerpColor(t.bg, t.far, 0.6)));
  ctx.fillStyle = g;
  ctx.fillRect(0, 0, p.w, p.h);
  for (let i = 0; i < 16; i++) {
    const x = p.rng.float() * p.w;
    const y = p.rng.float() * p.h;
    const lit = p.rng.chance(0.6);
    glowDot(ctx, x, y, p.unit * p.rng.range(0.18, 0.45), lit ? t.far : t.ink, lit ? 0.35 : 0.25);
  }
}

function gearPath(ctx: CanvasRenderingContext2D, d: Disc, teeth: number, depth: number, rot: number): void {
  const step = TAU / teeth;
  for (let i = 0; i < teeth; i++) {
    const a = rot + i * step;
    const pts: readonly (readonly [number, number])[] = [
      [d.r - depth, a],
      [d.r, a + step * 0.17],
      [d.r, a + step * 0.47],
      [d.r - depth, a + step * 0.64],
    ];
    for (const [r, b] of pts) {
      const x = d.x + Math.cos(b) * r;
      const y = d.y + Math.sin(b) * r;
      if (i === 0 && r === d.r - depth && b === a) ctx.moveTo(x, y);
      else ctx.lineTo(x, y);
    }
  }
  ctx.closePath();
}

function gear(p: Painter, t: Tones, d: Disc, spokes: number, rot: number): void {
  const ctx = p.ctx;
  const teeth = Math.max(8, Math.round(d.r / (0.026 * p.unit)));
  const depth = Math.min(d.r * 0.16, 0.03 * p.unit);
  const hole = d.r * 0.7;
  ctx.beginPath();
  gearPath(ctx, d, teeth, depth, rot);
  ctx.moveTo(d.x + hole, d.y);
  ctx.arc(d.x, d.y, hole, 0, TAU, true);
  ctx.fillStyle = rgba(t.brass);
  ctx.fill('evenodd');
  ctx.strokeStyle = rgba(t.brass);
  ctx.lineWidth = Math.max(3, d.r * 0.12);
  ctx.lineCap = 'butt';
  ctx.beginPath();
  for (let i = 0; i < spokes; i++) {
    const a = rot + (i / spokes) * TAU;
    ctx.moveTo(d.x, d.y);
    ctx.lineTo(d.x + Math.cos(a) * hole * 1.02, d.y + Math.sin(a) * hole * 1.02);
  }
  ctx.stroke();
  ctx.fillStyle = rgba(t.brass);
  ctx.beginPath();
  ctx.arc(d.x, d.y, d.r * 0.2, 0, TAU);
  ctx.fill();
  ctx.lineWidth = Math.max(2, d.r * 0.07);
  ctx.strokeStyle = rgba(t.brassLit, 0.4);
  ctx.beginPath();
  ctx.arc(d.x, d.y, d.r * 0.82, Math.PI * 1.05, Math.PI * 1.6);
  ctx.stroke();
  ctx.strokeStyle = rgba(t.ink, 0.45);
  ctx.beginPath();
  ctx.arc(d.x, d.y, d.r * 0.82, Math.PI * 0.05, Math.PI * 0.6);
  ctx.stroke();
  ctx.strokeStyle = rgba(t.ink, 0.9);
  ctx.lineWidth = Math.max(1.5, d.r * 0.022);
  ctx.lineJoin = 'round';
  ctx.beginPath();
  gearPath(ctx, d, teeth, depth, rot);
  ctx.stroke();
  sketchCircle(ctx, d.x, d.y, hole, p.rng, 0.015);
  sketchCircle(ctx, d.x, d.y, d.r * 0.2, p.rng, 0.03);
  ctx.fillStyle = rgba(t.ink, 0.85);
  ctx.beginPath();
  ctx.arc(d.x, d.y, Math.max(2, d.r * 0.06), 0, TAU);
  ctx.fill();
}

function escapement(p: Painter, t: Tones, d: Disc): void {
  const ctx = p.ctx;
  const teeth = 15;
  ctx.beginPath();
  for (let i = 0; i < teeth; i++) {
    const a = (i / teeth) * TAU;
    const b = a + (TAU / teeth) * 0.8;
    const tip = a + (TAU / teeth) * 0.15;
    if (i === 0) ctx.moveTo(d.x + Math.cos(a) * d.r * 0.78, d.y + Math.sin(a) * d.r * 0.78);
    else ctx.lineTo(d.x + Math.cos(a) * d.r * 0.78, d.y + Math.sin(a) * d.r * 0.78);
    ctx.lineTo(d.x + Math.cos(tip) * d.r, d.y + Math.sin(tip) * d.r);
    ctx.lineTo(d.x + Math.cos(b) * d.r * 0.78, d.y + Math.sin(b) * d.r * 0.78);
  }
  ctx.closePath();
  ctx.fillStyle = rgba(lerpColor(t.brass, t.bg, 0.2));
  ctx.fill();
  ctx.strokeStyle = rgba(t.ink, 0.9);
  ctx.lineWidth = Math.max(1.2, d.r * 0.03);
  ctx.stroke();
  ctx.fillStyle = rgba(lerpColor(t.bg, t.ink, 0.3));
  ctx.beginPath();
  ctx.arc(d.x, d.y, d.r * 0.55, 0, TAU);
  ctx.fill();
  ctx.stroke();
  ctx.strokeStyle = rgba(t.brass);
  ctx.lineWidth = Math.max(2, d.r * 0.1);
  ctx.beginPath();
  for (let i = 0; i < 4; i++) {
    const a = (i / 4) * TAU + 0.4;
    ctx.moveTo(d.x, d.y);
    ctx.lineTo(d.x + Math.cos(a) * d.r * 0.56, d.y + Math.sin(a) * d.r * 0.56);
  }
  ctx.stroke();
}

function clock(p: Painter, t: Tones, d: Disc, focal: boolean, hour: number, minute: number): void {
  const ctx = p.ctx;
  const face = focal ? t.ceil : lerpColor(t.far, t.ceil, 0.25);
  const bezel = focal ? t.brass : lerpColor(t.far, t.brass, 0.45);
  const mark = focal ? t.mark : lerpColor(face, t.mark, 0.55);
  if (focal) glowDot(ctx, d.x, d.y, d.r * 1.9, t.far, 0.6);
  ctx.fillStyle = rgba(bezel);
  ctx.beginPath();
  ctx.arc(d.x, d.y, d.r, 0, TAU);
  ctx.fill();
  ctx.fillStyle = rgba(face);
  ctx.beginPath();
  ctx.arc(d.x, d.y, d.r * 0.84, 0, TAU);
  ctx.fill();
  ctx.strokeStyle = rgba(mark, focal ? 0.75 : 0.5);
  ctx.lineCap = 'round';
  for (const major of [true, false]) {
    ctx.lineWidth = Math.max(1, d.r * (major ? 0.05 : 0.025));
    ctx.beginPath();
    for (let i = 0; i < 12; i++) {
      if ((i % 3 === 0) !== major) continue;
      if (!focal && !major) continue;
      const a = UP + (i / 12) * TAU;
      const r0 = major ? 0.6 : 0.68;
      ctx.moveTo(d.x + Math.cos(a) * d.r * r0, d.y + Math.sin(a) * d.r * r0);
      ctx.lineTo(d.x + Math.cos(a) * d.r * 0.76, d.y + Math.sin(a) * d.r * 0.76);
    }
    ctx.stroke();
  }
  ctx.strokeStyle = rgba(focal ? t.ink : mark, focal ? 0.85 : 0.6);
  ctx.lineWidth = Math.max(1.5, d.r * 0.07);
  ctx.beginPath();
  ctx.moveTo(d.x, d.y);
  ctx.lineTo(d.x + Math.cos(hour) * d.r * 0.4, d.y + Math.sin(hour) * d.r * 0.4);
  ctx.stroke();
  ctx.lineWidth = Math.max(1, d.r * 0.045);
  ctx.beginPath();
  ctx.moveTo(d.x, d.y);
  ctx.lineTo(d.x + Math.cos(minute) * d.r * 0.62, d.y + Math.sin(minute) * d.r * 0.62);
  ctx.stroke();
  ctx.fillStyle = rgba(focal ? t.brassLit : bezel);
  ctx.beginPath();
  ctx.arc(d.x, d.y, Math.max(1.5, d.r * 0.06), 0, TAU);
  ctx.fill();
  ctx.strokeStyle = rgba(t.ink, 0.9);
  ctx.lineWidth = Math.max(1.5, d.r * (focal ? 0.035 : 0.05));
  sketchCircle(ctx, d.x, d.y, d.r, p.rng, 0.012);
  if (!focal) return;
  ctx.lineWidth = Math.max(1, d.r * 0.02);
  sketchCircle(ctx, d.x, d.y, d.r * 0.84, p.rng, 0.01);
  ctx.strokeStyle = rgba(t.brassLit, 0.45);
  ctx.lineWidth = Math.max(1.5, d.r * 0.05);
  ctx.beginPath();
  ctx.arc(d.x, d.y, d.r * 0.92, Math.PI * 1.08, Math.PI * 1.55);
  ctx.stroke();
  ctx.fillStyle = rgba(t.brass);
  ctx.strokeStyle = rgba(t.ink, 0.9);
  ctx.lineWidth = Math.max(1.2, d.r * 0.03);
  ctx.beginPath();
  ctx.roundRect(d.x - d.r * 0.09, d.y - d.r * 1.16, d.r * 0.18, d.r * 0.18, d.r * 0.04);
  ctx.fill();
  ctx.stroke();
  sketchCircle(ctx, d.x, d.y - d.r * 1.28, d.r * 0.1, p.rng, 0.03);
}

function bench(p: Painter, t: Tones, top: number): void {
  const ctx = p.ctx;
  const deep = top + (p.h - top) * 0.42;
  ctx.fillStyle = rgba(t.wood);
  ctx.beginPath();
  ctx.moveTo(-10, top + p.rng.range(-2, 2));
  for (let x = 0; x <= p.w + 40; x += 40) ctx.lineTo(x, top + p.rng.range(-1.5, 1.5));
  ctx.lineTo(p.w + 10, p.h + 10);
  ctx.lineTo(-10, p.h + 10);
  ctx.closePath();
  ctx.fill();
  ctx.fillStyle = rgba(lerpColor(t.wood, t.ink, 0.45));
  ctx.fillRect(0, deep, p.w, p.h - deep);
  ctx.strokeStyle = rgba(lerpColor(t.wood, t.brass, 0.5), 0.55);
  ctx.lineWidth = 2;
  sketchLine(ctx, 0, top, p.w, top, p.rng, 0.002);
  ctx.strokeStyle = rgba(t.ink, 0.7);
  sketchLine(ctx, 0, deep, p.w, deep, p.rng, 0.002);
  ctx.strokeStyle = rgba(lerpColor(t.wood, t.ink, 0.5), 0.3);
  ctx.lineWidth = 1;
  for (let i = 0; i < 4; i++) {
    const y = top + (deep - top) * p.rng.range(0.2, 0.85);
    const x0 = p.rng.range(-0.1, 0.5) * p.w;
    sketchLine(ctx, x0, y, x0 + p.w * p.rng.range(0.25, 0.6), y + p.rng.range(-3, 3), p.rng, 0.01);
  }
}

function loupe(p: Painter, t: Tones, d: Disc): void {
  const ctx = p.ctx;
  const a = 0.35;
  const hx = d.x + Math.cos(a) * d.r;
  const hy = d.y + Math.sin(a) * d.r * 0.45;
  ctx.lineCap = 'round';
  ctx.strokeStyle = rgba(t.ink, 0.9);
  ctx.lineWidth = d.r * 0.34;
  ctx.beginPath();
  ctx.moveTo(hx, hy);
  ctx.lineTo(hx + d.r * 1.3, hy + d.r * 0.25);
  ctx.stroke();
  ctx.strokeStyle = rgba(lerpColor(t.wood, t.ink, 0.2));
  ctx.lineWidth = d.r * 0.24;
  ctx.stroke();
  ctx.fillStyle = rgba(lerpColor(t.bg, t.mark, 0.12), 0.8);
  ctx.beginPath();
  ctx.ellipse(d.x, d.y, d.r, d.r * 0.45, 0, 0, TAU);
  ctx.fill();
  ctx.strokeStyle = rgba(t.brass);
  ctx.lineWidth = d.r * 0.16;
  ctx.stroke();
  ctx.strokeStyle = rgba(t.ink, 0.9);
  ctx.lineWidth = Math.max(1.2, d.r * 0.04);
  ctx.beginPath();
  ctx.ellipse(d.x, d.y, d.r * 1.08, d.r * 0.53, 0, 0, TAU);
  ctx.stroke();
  ctx.strokeStyle = rgba(t.mark, 0.3);
  ctx.lineWidth = Math.max(1.5, d.r * 0.08);
  ctx.beginPath();
  ctx.ellipse(d.x, d.y, d.r * 0.7, d.r * 0.28, 0, Math.PI * 1.1, Math.PI * 1.45);
  ctx.stroke();
}

function screws(p: Painter, t: Tones, d: Disc): void {
  const ctx = p.ctx;
  for (let i = 0; i < 4; i++) {
    const x = d.x + i * d.r * 3 + p.rng.range(-d.r, d.r);
    const y = d.y + p.rng.range(-d.r, d.r);
    const r = d.r * p.rng.range(0.7, 1.1);
    ctx.fillStyle = rgba(lerpColor(t.wood, t.brass, 0.7));
    ctx.beginPath();
    ctx.arc(x, y, r, 0, TAU);
    ctx.fill();
    ctx.strokeStyle = rgba(t.ink, 0.9);
    ctx.lineWidth = Math.max(1, r * 0.25);
    ctx.stroke();
    const a = p.rng.float() * Math.PI;
    ctx.beginPath();
    ctx.moveTo(x - Math.cos(a) * r * 0.7, y - Math.sin(a) * r * 0.7);
    ctx.lineTo(x + Math.cos(a) * r * 0.7, y + Math.sin(a) * r * 0.7);
    ctx.stroke();
  }
}

function spring(p: Painter, t: Tones, d: Disc): void {
  const ctx = p.ctx;
  const len = d.r * 4;
  const loops = 7;
  ctx.strokeStyle = rgba(lerpColor(t.bg, t.mark, 0.55), 0.7);
  ctx.lineWidth = Math.max(1.2, d.r * 0.09);
  ctx.beginPath();
  for (let i = 0; i <= 84; i++) {
    const s = i / 84;
    const a = s * loops * TAU;
    const x = d.x + s * len + Math.cos(a) * d.r * 0.3;
    const y = d.y + Math.sin(a) * d.r * 0.5;
    if (i === 0) ctx.moveTo(x, y);
    else ctx.lineTo(x, y);
  }
  ctx.stroke();
}

function tweezers(p: Painter, t: Tones, d: Disc): void {
  const ctx = p.ctx;
  ctx.strokeStyle = rgba(lerpColor(t.bg, t.mark, 0.45), 0.75);
  ctx.lineWidth = Math.max(1.5, d.r * 0.05);
  ctx.lineCap = 'round';
  sketchLine(ctx, d.x, d.y, d.x + d.r, d.y - d.r * 0.08, p.rng, 0.02);
  sketchLine(ctx, d.x, d.y, d.x + d.r, d.y + d.r * 0.08, p.rng, 0.02);
}

export function paint(p: Painter, look: Look): void {
  const pal = look.palette;
  const t: Tones = {
    bg: pal.bg,
    far: hsl(look.hue, 30, 16),
    ceil: look.ceil,
    ink: pal.ink,
    brass: lerpColor(pal.bg, pal.held, 0.26),
    brassLit: lerpColor(pal.bg, pal.held, 0.6),
    wood: lerpColor(pal.bg, pal.spot, 0.13),
    mark: pal.path,
  };
  const s = sceneOf(p);
  wall(p, t);
  for (const c of s.clocks) clock(p, t, c, false, p.rng.float() * TAU, p.rng.float() * TAU);
  for (const [i, tr] of s.trains.entries()) {
    const rot = i * 0.4;
    gear(p, t, tr.gear, 5, rot);
    if (p.level >= 2) gear(p, t, tr.pinion, 4, rot + 0.2);
    if (p.level >= 3) escapement(p, t, tr.wheel);
  }
  clock(p, t, s.focal, true, UP + (10 / 12) * TAU, UP + (2 / 12) * TAU);
  bench(p, t, s.top);
  loupe(p, t, s.loupe);
  screws(p, t, s.screws);
  spring(p, t, s.spring);
  if (p.level >= 3) tweezers(p, t, s.tweezers);
  calmCenter(p, pal.bg, 0.6, 1.15);
  grain(p, 0.05, true);
  vignette(p, pal.ink, 0.45);
}
